import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, XCircle, MessageCircle, Ban } from "lucide-react";
import { Seo } from "@/components/Seo";
import { buildStockWhatsAppUrl } from "@/lib/whatsapp";

const USAGE = [
  "Aktivasi produk dilakukan melalui website atau aplikasi resmi dari layanan yang dipilih.",
  "Setiap akun maksimal digunakan pada satu perangkat, kecuali informasi produk menyatakan ketentuan yang berbeda.",
  "Akun yang diberikan bersifat private dan tidak boleh dibagikan, dipindahtangankan, atau digunakan bersama pihak lain.",
  "Pelanggan wajib mengikuti petunjuk aktivasi dan penggunaan yang diberikan melalui WhatsApp.",
];

const SCOPE = [
  "Garansi berlaku selama tujuh hari sejak aktivasi dinyatakan berhasil.",
  "Garansi hanya mencakup kendala teknis yang berasal dari layanan atau akun yang diberikan oleh Nofina Movie.",
  "Untuk mengajukan bantuan, pelanggan harus menyertakan nama produk, kode produk, detail kendala, dan bukti pendukung yang relevan melalui WhatsApp Nofina Movie.",
  "Nofina Movie akan memeriksa laporan dan memberikan penanganan sesuai kondisi produk serta hasil pemeriksaan.",
];

const EXCLUSIONS = [
  "Akun dibagikan atau digunakan pada lebih dari satu perangkat.",
  "Pelanggan mengubah data akun, password, profil, metode pembayaran, atau pengaturan penting tanpa instruksi.",
  "Akun disalahgunakan atau digunakan dengan cara yang melanggar ketentuan layanan terkait.",
  "Kendala disebabkan perangkat, jaringan internet, aplikasi, atau tindakan pelanggan.",
  "Akun terkena suspend atau banned akibat penggunaan atau pelanggaran yang berada di luar kendali Nofina Movie.",
  "Laporan disampaikan setelah masa garansi berakhir.",
];

function Card({ icon: Icon, tone, title, children }) {
  return (
    <section className="rounded-2xl border border-nm-border bg-nm-surface p-5 sm:p-6">
      <div className="flex items-center gap-2.5">
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <h2 className="text-lg font-bold text-nm-text">{title}</h2>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function GaransiPage() {
  const waUrl = buildStockWhatsAppUrl();

  return (
    <>
      <Seo
        title="Ketentuan Garansi"
        description="Ketentuan garansi Nofina Movie: masa berlaku tujuh hari sejak aktivasi berhasil, cakupan kendala teknis, pengecualian, serta kebijakan pembatalan dan refund."
      />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <header className="max-w-2xl">
          <h1 className="text-3xl font-extrabold text-nm-text sm:text-4xl">
            Ketentuan Garansi Nofina Movie
          </h1>
          <p className="mt-3 text-nm-muted">
            Garansi diberikan untuk membantu pelanggan apabila terjadi kendala
            teknis pada produk digital yang telah berhasil diaktifkan. Bacalah
            ketentuan berikut sebelum menggunakan produk.
          </p>
        </header>

        <div className="mt-8 space-y-5">
          <Card icon={ShieldCheck} tone="bg-nm-red/10 text-nm-red" title="Ketentuan penggunaan">
            <ul className="space-y-2.5">
              {USAGE.map((t, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-nm-muted">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-nm-green" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card icon={CheckCircle2} tone="bg-nm-green-soft text-nm-green" title="Masa dan cakupan garansi">
            <ul className="space-y-2.5">
              {SCOPE.map((t, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-nm-muted">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-nm-green" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card icon={XCircle} tone="bg-nm-surface2 text-nm-muted" title="Garansi tidak berlaku apabila">
            <ul className="space-y-2.5">
              {EXCLUSIONS.map((t, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-nm-muted">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-nm-red" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card icon={Ban} tone="bg-nm-surface2 text-nm-muted" title="Pembatalan dan pengembalian dana">
            <p className="text-sm text-nm-muted">
              Produk digital yang sudah berhasil diaktifkan tidak dapat
              dibatalkan dan tidak dapat direfund. Karena itu, pelanggan wajib
              memastikan produk, durasi, dan perangkat yang digunakan sudah
              benar sebelum aktivasi.
            </p>
          </Card>
        </div>

        <div className="mt-8 rounded-2xl border border-nm-border bg-nm-surface p-6 text-center">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="garansi-whatsapp"
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-wa px-6 font-semibold text-white shadow-lg shadow-wa/20 hover:bg-wa-hover sm:w-auto"
          >
            <MessageCircle className="h-5 w-5" />
            Cek Stok via WhatsApp
          </a>
          <p className="mt-3 text-xs text-nm-muted">
            Baca juga{" "}
            <Link to="/ketentuan" className="font-semibold text-nm-red hover:underline">
              Ketentuan Layanan
            </Link>
            .
          </p>
        </div>
      </div>
    </>
  );
}
